import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Modal,
  SearchInput,
  Switch,
} from '@/components/ui';
import { KPICard } from './components/KPICard';
import {
  type FamilyContextHousehold,
  type FamilyContextMode,
  useAdminFamilyContext,
} from './hooks/useAdminFamilyContext';

/** "Oct 2, 2026" from an ISO timestamp; the raw value if unparseable. */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function householdLabel(h: FamilyContextHousehold): string {
  return h.name || h.primary_name || h.primary_email || h.household_id;
}

function matches(h: FamilyContextHousehold, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return [h.name, h.primary_name, h.primary_email, h.household_id].some((v) =>
    v?.toLowerCase().includes(q),
  );
}

/**
 * Family Context rollout: which households get context-aware extraction.
 *
 * EXTRACTION_CONTEXT_LEVEL is the ceiling (0 = off for everyone). Under it the
 * mode decides: 'canary' gives context only to the households switched on
 * below; 'all' gives it to every household. Households without it keep the
 * extraction they had before the feature.
 */
export function AdminFamilyContext({ password }: { password: string }) {
  const { status, data, error, saving, saveError, save, refetch } =
    useAdminFamilyContext(password);
  const [query, setQuery] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);

  const visible = useMemo(
    () => (data?.households ?? []).filter((h) => matches(h, query)),
    [data, query],
  );

  if (status === 'loading' && !data) {
    return (
      <div className="text-muted-foreground">
        Loading family context settings…
      </div>
    );
  }
  if (status === 'error' && !data) {
    return (
      <Card className="border-danger">
        <div className="text-sm text-danger">
          Failed to load family context settings: {error}
        </div>
        <Button
          variant="secondary"
          onClick={refetch}
          className="mt-3"
        >
          Retry
        </Button>
      </Card>
    );
  }
  if (!data) return null;

  const enabledCount = data.households.filter((h) => h.enabled).length;
  const off = data.context_level === 0;
  const setMode = (mode: FamilyContextMode) => save({ mode });

  return (
    <div className="flex flex-col gap-6 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold m-0">Family Context</h2>
        <Button
          variant="secondary"
          onClick={refetch}
          disabled={status === 'loading'}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${status === 'loading' ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <p className="text-sm text-muted-foreground m-0">
        Families with Family Context get extraction that knows their children,
        their schedule and what they corrected before. Everyone else keeps the
        extraction they had before. Changes apply to the next email processed.
      </p>

      {off ? (
        <div
          className="rounded-md border border-warning bg-warning-light p-4 text-sm text-foreground"
          data-testid="level-off-warning"
        >
          EXTRACTION_CONTEXT_LEVEL is 0 in this environment, so no family gets
          context, whatever the switches below say.
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPICard
          label="Context level"
          value={data.context_level}
          hint="EXTRACTION_CONTEXT_LEVEL — the ceiling for every family"
        />
        <KPICard
          label="Who gets it"
          value={data.mode === 'all' ? 'Everyone' : 'Selected only'}
          hint={
            data.mode === 'all'
              ? `All ${data.households.length} families`
              : `${enabledCount} of ${data.households.length} families switched on`
          }
        />
        <Card
          padding="md"
          className="flex flex-col gap-2 justify-center"
        >
          <span className="text-sm text-muted-foreground">Rollout</span>
          {data.mode === 'all' ? (
            <Button
              variant="secondary"
              onClick={() => setMode('canary')}
              disabled={saving}
            >
              Back to selected families only
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => setConfirmAll(true)}
              disabled={saving}
            >
              Turn on for everyone
            </Button>
          )}
        </Card>
      </div>

      {saveError ? (
        <div
          className="text-sm text-danger"
          role="alert"
        >
          Could not save the change: {saveError}
        </div>
      ) : null}

      <Card padding="none">
        <div className="p-3 border-b border-border">
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by family, name or email"
            aria-label="Search families"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-3 font-medium">Family</th>
                <th className="p-3 font-medium">Primary</th>
                <th className="p-3 font-medium text-right">Members</th>
                <th className="p-3 font-medium">Switched on</th>
                <th className="p-3 font-medium">Family Context</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((h) => {
                const label = householdLabel(h);
                return (
                  <tr
                    key={h.household_id}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="p-3">{label}</td>
                    <td className="p-3 text-muted-foreground">
                      {h.primary_email ?? '—'}
                    </td>
                    <td className="p-3 text-right">{h.member_count}</td>
                    <td className="p-3 text-muted-foreground">
                      {h.enabled ? formatDate(h.enabled_at) : '—'}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          size="sm"
                          checked={h.enabled}
                          disabled={saving}
                          aria-label={`Family Context for ${label}`}
                          onChange={(e) =>
                            save({
                              household_id: h.household_id,
                              enabled: e.target.checked,
                            })
                          }
                        />
                        {data.mode === 'all' && !h.enabled ? (
                          <Badge variant="info">On via everyone</Badge>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="p-6 text-center text-muted-foreground"
                  >
                    No families match.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        title="Turn on Family Context for everyone?"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-foreground m-0">
            Every family — {data.households.length} today, and every new one —
            will get Family Context on their next email. The switches below are
            kept, so going back to selected families restores exactly this list.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setConfirmAll(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={saving}
              onClick={async () => {
                if (await setMode('all')) setConfirmAll(false);
              }}
            >
              Turn on for everyone
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
